import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  TextField,
  Container,
  Typography,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import catImage from "../images/homecat1.avif";
import dogImage from "../images/homeimg.avif";
import Navbar from "./Navbar.js";
import axiosInstance from "../api/axios";
import { useAuth } from "../components/AuthContext";
import { Helmet } from "react-helmet-async";
import PetLoader from "./Petloader.js";

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState("Logging you in...");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [errors, setErrors] = useState({});

  const { email, password } = formData;
  const navigate = useNavigate();

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoaderMessage("Waking up the server...");
    setLoading(true);

    // Stagger loader messages for long cold starts
    const messages = [
      { text: "Waking up the server...", delay: 0 },
      { text: "Cold start — hang tight! 🐾", delay: 5000 },
      { text: "Almost there...", delay: 18000 },
      { text: "Just a few more seconds...", delay: 35000 },
    ];
    const timers = messages.map(({ text, delay }) =>
      setTimeout(() => setLoaderMessage(text), delay),
    );

    try {
      const res = await axiosInstance.post("/login", { email, password });

      timers.forEach(clearTimeout);

      if (res.status === 200) {
        setLoaderMessage("Success! Redirecting...");
        localStorage.setItem("userEmail", email);
        localStorage.setItem("sessionId", res.data.sessionId);
        localStorage.setItem("bulkAccess", res.data.bulk_access ? "true" : "false");
        localStorage.setItem("bulkAccessStatus", res.data.bulk_access_status || "active");
        localStorage.setItem("mustChangePassword", res.data.must_change_password ? "true" : "false");
        login();
        setSnackbar({
          open: true,
          message: "Login successful!",
          severity: "success",
        });
        setTimeout(() => navigate("/furhome"), 600);
      } else {
        timers.forEach(clearTimeout);
        setLoading(false);
        setSnackbar({
          open: true,
          message: "Login failed. Please check your credentials.",
          severity: "error",
        });
      }
    } catch (err) {
      timers.forEach(clearTimeout);
      setLoading(false);

      if (err.response?.data?.errors) {
        const backendErrors = err.response.data.errors.reduce((acc, error) => {
          acc[error.param] = error.msg;
          return acc;
        }, {});
        setErrors(backendErrors);
      } else if (err.response?.data?.detail) {
        setSnackbar({
          open: true,
          message: err.response.data.detail,
          severity: "error",
        });
      } else if (err.request) {
        setSnackbar({
          open: true,
          message:
            "No response from server. Please check your internet connection.",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: "An error occurred. Please try again later.",
          severity: "error",
        });
      }
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };
  const buttonVariants = {
    hover: { backgroundColor: "#BDA697", transition: { duration: 0.3 } },
  };
  const imageVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: { opacity: 1, x: 0, transition: { duration: 1 } },
  };
  const linkVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  useEffect(() => {
    const auth = sessionStorage.getItem("isAuthenticated");
    const email = localStorage.getItem("userEmail");
    const id = localStorage.getItem("sessionId");
    if (auth === "true" && email && id) {
      navigate("/furhome");
    } else {
      navigate("/login");
    }
  }, [navigate]);

  return (
    <section className="relative text-center mt-20 pt-16">
      <Helmet>
        <title>Login – Furbabies.AI</title>
        <meta
          name="description"
          content="Log in to your Furbabies.AI account to check your pet's health, track nutrition, and access AI-powered care from anywhere."
        />
      </Helmet>

      {/* Production-grade loader */}
      <PetLoader isVisible={loading} message={loaderMessage} />

      <Navbar />
      <Container maxWidth="sm" className="mt-14">
        <motion.h1
          variants={textVariants}
          initial="hidden"
          animate="visible"
          className="text-5xl lg:text-[60px] lg:leading-[70px] font-bold text-gray-900"
        >
          Welcome Back!
        </motion.h1>
        <motion.p
          variants={textVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="mt-4 text-xl lg:text-[18px] lg:leading-[28px] poppins-text text-gray-600"
        >
          Login to access personalized veterinary care for your beloved furry
          companions.
        </motion.p>

        <form
          className="mt-6 text-left"
          onSubmit={onSubmit}
          aria-label="Login form"
        >
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.4 }}
            className="mb-4"
          >
            <Typography variant="h6" className="mb-2 text-left">
              Email
            </Typography>
            <TextField
              variant="outlined"
              name="email"
              placeholder="Email"
              fullWidth
              className="mb-4"
              value={email}
              onChange={onChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "1rem",
                  backgroundColor: "#f5f5f5",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "#cbd5e1" },
                },
              }}
            />
          </motion.div>

          <motion.div
            variants={textVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.6 }}
            className="mb-4"
          >
            <Typography variant="h6" className="mb-2 text-left">
              Password
            </Typography>
            {/* <TextField
              type="password"
              variant="outlined"
              placeholder="Password"
              fullWidth
              className="mb-4"
              name="password"
              value={password}
              onChange={onChange}
              error={Boolean(errors.password)}
              helperText={errors.password}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "1rem",
                  backgroundColor: "#f5f5f5",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "#cbd5e1" },
                },
              }}
            /> */}
            <TextField
              type={showPassword ? "text" : "password"}
              variant="outlined"
              placeholder="Password"
              fullWidth
              className="mb-4"
              name="password"
              value={password}
              onChange={onChange}
              error={Boolean(errors.password)}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "1rem",
                  backgroundColor: "#f5f5f5",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "#cbd5e1" },
                },
              }}
            />
          </motion.div>

          <motion.div whileHover="hover" className="mt-6 flex justify-center">
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              disabled={loading}
              className="mt-6 text-xl lg:text-[20px] lg:leading-[26px] px-6 py-3 bg-[#967d6c] text-white font-bold rounded-full w-full disabled:opacity-60"
            >
              Login
            </motion.button>
          </motion.div>
        </form>

        <motion.p
          className="signup-text text-lg lg:text-[18px] poppins-text lg:leading-[28px] mt-4"
          variants={linkVariants}
          initial="hidden"
          animate="visible"
        >
          Don't have an account?{" "}
          <Link
            to="/signup"
            className="signup-link text-[#967d6c] hover:underline"
          >
            Sign up
          </Link>
        </motion.p>
        <motion.p
          className="forgot-password-text text-lg lg:text-[18px] poppins-text lg:leading-[28px] mt-2 mb-4"
          variants={linkVariants}
          initial="hidden"
          animate="visible"
        >
          Forgot your password?{" "}
          <Link
            to="/forgotpassword"
            className="forgot-password-link text-[#967d6c] hover:underline"
          >
            Reset it here
          </Link>
        </motion.p>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <motion.div
        className="absolute left-0 top-20 hidden xl:block"
        variants={imageVariants}
        initial="hidden"
        animate="visible"
      >
        <img
          src={catImage}
          alt="Cat"
          className="h-[36rem] mx-auto"
          loading="lazy"
        />
        <div className="absolute left-60 bottom-20 transform -translate-x-1/2 hidden sm:block">
          <div className="text-6xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
        <div className="absolute left-64 top-80 transform translate-x-1/2 hidden xl:block">
          <div className="text-4xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute right-0 top-24 hidden xl:block"
        variants={{ ...imageVariants, hidden: { opacity: 0, x: 100 } }}
        initial="hidden"
        animate="visible"
      >
        <img
          src={dogImage}
          alt="Dog"
          className="h-[34rem] mx-auto"
          loading="lazy"
        />
        <div className="absolute right-24 bottom-2 transform -translate-x-1/2 hidden xl:block">
          <div className="text-4xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
        <div className="absolute right-64 bottom-16 transform translate-x-1/2 hidden sm:block">
          <div className="text-6xl text-[#bda697] animate-paw-left-right">
            <i className="fa fa-paw"></i>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Login;
